export interface TemplateParts {
  html: string;
  css: string;
  js: string;
}

export type TemplateEditorTab = 'html' | 'css' | 'js';

const STYLE_RE = /<style[^>]*>([\s\S]*?)<\/style>/gi;
const SCRIPT_RE = /<script(?![^>]*\bsrc\b)[^>]*>([\s\S]*?)<\/script>/gi;
const BODY_RE = /<body[^>]*>([\s\S]*?)<\/body>/i;
const HEAD_RE = /<head[^>]*>([\s\S]*?)<\/head>/i;

function extractAll(re: RegExp, source: string): string[] {
  const results: string[] = [];
  const globalRe = new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`);
  let match: RegExpExecArray | null;
  while ((match = globalRe.exec(source)) !== null) {
    results.push(match[1].trim());
  }
  return results;
}

function stripInlineAssets(source: string): string {
  return source
    .replace(STYLE_RE, '')
    .replace(SCRIPT_RE, '')
    .trim();
}

/** Split a full HTML template into editable HTML / CSS / JS sections */
export function splitTemplate(htmlTemplate: string): TemplateParts {
  const styles = extractAll(STYLE_RE, htmlTemplate);
  const scripts = extractAll(SCRIPT_RE, htmlTemplate);

  const css = styles.join('\n\n');
  const js = scripts.join('\n\n');

  const bodyMatch = htmlTemplate.match(BODY_RE);
  if (bodyMatch) {
    return {
      html: stripInlineAssets(bodyMatch[1]),
      css,
      js,
    };
  }

  const headMatch = htmlTemplate.match(HEAD_RE);
  const hasDocumentShell = /<!DOCTYPE/i.test(htmlTemplate) || /<html[\s>]/i.test(htmlTemplate);

  if (hasDocumentShell) {
    let html = htmlTemplate
      .replace(/<!DOCTYPE[^>]*>/i, '')
      .replace(/<\/?html[^>]*>/gi, '')
      .replace(HEAD_RE, '')
      .replace(/<\/?body[^>]*>/gi, '');

    html = stripInlineAssets(html).trim();
    return { html, css, js };
  }

  return {
    html: stripInlineAssets(htmlTemplate),
    css,
    js,
  };
}

/** Combine editable sections back into a single HTML document for preview / storage */
export function combineTemplateParts(parts: TemplateParts): string {
  const html = parts.html.trim();
  const css = parts.css.trim();
  const js = parts.js.trim();

  const hasFullDocument =
    /<!DOCTYPE/i.test(html) ||
    (/^<html[\s>]/i.test(html) && /<\/html>\s*$/i.test(html));

  if (hasFullDocument) {
    let combined = html;
    if (css) {
      const styleBlock = `<style>\n${css}\n</style>`;
      if (/<\/head>/i.test(combined)) {
        combined = combined.replace(/<\/head>/i, `${styleBlock}\n</head>`);
      } else if (/<head[^>]*>/i.test(combined)) {
        combined = combined.replace(/<head([^>]*)>/i, `<head$1>\n${styleBlock}`);
      } else {
        combined = combined.replace(/<html([^>]*)>/i, `<html$1>\n<head>\n${styleBlock}\n</head>`);
      }
    }
    if (js) {
      const scriptBlock = `<script>\n${js}\n</script>`;
      if (/<\/body>/i.test(combined)) {
        combined = combined.replace(/<\/body>/i, `${scriptBlock}\n</body>`);
      } else {
        combined = `${combined}\n${scriptBlock}`;
      }
    }
    return combined;
  }

  const cssBlock = css ? `<style>\n${css}\n</style>\n` : '';
  const jsBlock = js ? `\n<script>\n${js}\n</script>` : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${cssBlock}</head>
<body>
${html}
${jsBlock}
</body>
</html>`;
}

export function getPartLineCount(part: string): number {
  if (!part) return 1;
  return part.split('\n').length;
}
